<template>
    <div v-if="isChanged" class="mass-replacement">
        <div v-if="canMassEdit" class="mass-replacement__trigger">
            <slot name="trigger" :open="open">
                <button
                    v-t="'massReplacement.trigger'"
                    class="button button--blank"
                    type="button"
                    @click="open"
                />
            </slot>
        </div>
        <portal to="modals">
            <Modal
                v-if="opened"
                :title="title"
                :modal-class="
                    `mass-replacement__modal ${viewAll ? 'mass-replacement__modal--full-list' : ''}`
                "
                @close="ok"
            >
                <template #content>
                    <Spinner v-if="loading" class="spinner--opaque">{{ loadingText }}</Spinner>

                    <template v-if="stage === 'fetched'">
                        <div class="mass-replacement__form">
                            <div class="mass-replacement__form-row">
                                <div
                                    v-t="'massReplacement.target'"
                                    class="mass-replacement__form-label"
                                />
                                <FormInput
                                    v-model="replaceParams.word"
                                    class="mass-replacement__form-field"
                                    editable
                                    @change="onReplaceParamsWordChange"
                                />
                                <div
                                    v-if="replacements.length"
                                    class="mass-replacement__form-position"
                                    v-text="
                                        $t('massReplacement.position', {
                                            n: position + 1,
                                            total: replacements.length
                                        })
                                    "
                                />
                            </div>
                            <div class="mass-replacement__form-row">
                                <div
                                    v-t="'massReplacement.replacement'"
                                    class="mass-replacement__form-label"
                                />
                                <FormInput
                                    :model-value="replaceParams.replace"
                                    class="mass-replacement__form-field"
                                    readonly
                                    disabled
                                />
                                <div v-if="!viewAll" class="mass-replacement__form-diffmode">
                                    <Checkbox v-model="diffMode">
                                        {{ $t('massReplacement.diffSwitch') }}
                                    </Checkbox>
                                </div>
                            </div>
                            <div
                                v-if="replacements.length"
                                class="mass-replacement__form-row mass-replacement__form-row--buttons"
                            >
                                <template v-if="viewAll">
                                    <button
                                        v-t="'massReplacement.buttons.replaceAll'"
                                        type="button"
                                        class="button"
                                        @click="replaceAll"
                                    />
                                </template>
                                <template v-else>
                                    <button
                                        v-t="'massReplacement.buttons.replace'"
                                        type="button"
                                        class="button button--outlined"
                                        @click="replace()"
                                    />
                                    <button
                                        v-t="'massReplacement.buttons.replaceAll'"
                                        type="button"
                                        class="button button--outlined"
                                        @click="replaceAll"
                                    />
                                    <button
                                        v-if="replacements.length > 1"
                                        v-t="'massReplacement.buttons.fullList'"
                                        type="button"
                                        class="button"
                                        @click="viewFullList"
                                    />
                                    <button
                                        v-t="'massReplacement.buttons.previous'"
                                        type="button"
                                        :class="['button', { 'is-disabled': !hasSiblings }]"
                                        :disabled="!hasSiblings"
                                        @click="prevMatch"
                                    />
                                    <button
                                        v-t="'massReplacement.buttons.next'"
                                        type="button"
                                        :class="['button', { 'is-disabled': !hasSiblings }]"
                                        :disabled="!hasSiblings"
                                        @click="nextMatch"
                                    />
                                </template>
                            </div>
                            <div v-else class="mass-replacement__sentences">
                                <div style="font-size: 14px;" class="mass-replacement-sentence">
                                    Nothing found to replace, try entering another word
                                </div>
                            </div>
                        </div>

                        <div v-if="sentences.length" class="mass-replacement__sentences">
                            <MassReplacementSentence
                                v-for="sentence in sentences"
                                :key="sentence.id"
                                :replacement="sentence"
                                :diff-mode="diffMode"
                                :show-actions="viewAll"
                                @replace="replace"
                            />
                        </div>
                    </template>
                    <div v-if="stage === 'success'" class="mass-replacement__text">
                        <IconTick class="mass-replacement__success-icon" />
                        <p v-t="'massReplacement.success'" />
                        <button v-t="'misc.close'" class="button button--big" @click.prevent="ok" />
                    </div>
                </template>
            </Modal>
        </portal>
    </div>
</template>

<script>
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { Checkbox, FormInput } from '@speechki-frontend/shared/ui/forms';
import cloneDeep from 'lodash/cloneDeep';
import { mapGetters, mapState, mapActions } from 'vuex';
import { fetchReplacements, replaceSinglePart, replaceAllParts } from '../../api/books';
import MassReplacementSentence from './MassReplacementSentence';
import { IconTick } from '@speechki-frontend/shared/ui/icons';

function isNotInTags({ parent }) {
    if (parent.name === 'speak') return true;
    if (parent.children.length === 1 && parent.children[0].type === 'word') return true;

    return false;
}

function isSameSelection(a, b) {
    return (
        a.id === b.id ||
        (a.children && a.children.length === 1 && a.children[0].id === b.id) ||
        (b.children && b.children.length === 1 && b.children[0].id === a.id)
    );
}

function getSsml(node) {
    if (!node) return '';

    if (node.type === 'tag') {
        const { name, attributes, children } = node;
        return `<${name} ${Object.entries(attributes)
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ')}>${children[0].source}</${name}>`;
    }

    return node.source;
}

export default {
    name: 'MassReplacement',
    components: {
        Modal,
        Checkbox,
        FormInput,
        MassReplacementSentence,
        IconTick
    },

    props: {
        selection: {
            type: Array,
            required: true
        },
        speaker: {
            type: Object,
            required: true
        },
        speakerSettings: {
            type: Object,
            required: true
        }
    },

    inject: ['registerWsHandler', 'removeWsHandler'],

    data() {
        return {
            opened: false,
            loading: false,
            position: 0,
            diffMode: false,
            viewAll: false,
            replacements: [],
            initialSelection: undefined,
            stage: 'initial',
            generatedCount: 0,
            singleReplacement: false,
            shouldReload: false,
            total: 0,
            completed: 0,
            timeout: undefined
        };
    },

    computed: {
        ...mapGetters(['isBookTrial']),
        ...mapState({
            bookId: ({ book }) => book.book.id
        }),

        title() {
            return this.$t(this.stage === 'success' ? 'misc.done' : 'massReplacement.title');
        },

        firstElement() {
            if (this.selection.length) return this.selection[0];

            return undefined;
        },

        canMassEdit() {
            const { selection, speakerSettings, firstElement } = this;

            if (
                speakerSettings &&
                selection &&
                selection.length === 1 &&
                isNotInTags(firstElement)
            ) {
                return true;
            }

            return false;
        },

        initialSsml() {
            return getSsml(this.initialSelection);
        },

        replacementSsml() {
            return getSsml(this.firstElement);
        },

        isChanged() {
            return this.initialSsml && this.initialSsml !== this.replacementSsml;
        },

        replaceParams() {
            return {
                word: this.initialSsml,
                replace: this.replacementSsml,
                speaker: this.speaker.id
            };
        },

        activeSentence() {
            return this.replacements[this.position];
        },

        hasSiblings() {
            return this.replacements.length > 1;
        },

        sentences() {
            if (!this.replacements.length) return [];
            if (this.viewAll) return this.replacements;
            return [this.activeSentence];
        },

        loadingText() {
            if (this.stage === 'initial') {
                return '';
            }

            if (this.stage === 'fetching') {
                return 'Fetching replacements';
            }

            return this.$t('massReplacement.generation', { n: this.completed, total: this.total });
        }
    },

    watch: {
        selection: {
            deep: true,
            immediate: true,
            handler(newv, oldv) {
                if (!this.canMassEdit) return;

                if (!oldv) {
                    this.initialSelection = cloneDeep(this.firstElement);
                    return;
                }

                const [newFirst] = newv;
                const [oldFirst] = oldv;

                if (isSameSelection(newFirst, oldFirst)) {
                    return;
                }

                this.initialSelection = cloneDeep(this.firstElement);
            }
        },

        opened(value) {
            if (!value) {
                this.viewAll = false;
                this.diffMode = false;
                this.stage = 'initial';
            }
        }
    },

    created() {
        this.registerWsHandler('record_process_book_part_update', this.onMessage);
    },

    beforeUnmount() {
        this.removeWsHandler('record_process_book_part_update', this.onMessage);
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    },

    methods: {
        ...mapActions(['pushToast']),

        open() {
            this.opened = true;
            this.fetchReplacements();
        },

        async fetchReplacements() {
            if (!this.initialSsml || !this.replacementSsml) return;

            try {
                this.stage = 'fetching';
                this.loading = true;
                this.error = null;

                this.replacements = await fetchReplacements(this.bookId, this.replaceParams);
                this.total = this.replacements.length;

                this.stage = 'fetched';
            } catch (e) {
                this.error = e;
                this.stage = 'initial';
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    title: this.$t('errors.generationError')
                });
            } finally {
                this.loading = false;
            }
        },

        async replace(replacement) {
            if (!replacement) {
                replacement = this.activeSentence;
            }

            this.error = null;

            try {
                const response = await replaceSinglePart(this.bookId, {
                    ...this.replaceParams,
                    book_part: replacement.id
                });

                if (!response.status) {
                    this.error = [{ detail: response.detail }];
                    return;
                }

                replacement['_replacing'] = true;

                this.timeout = setTimeout(() => {
                    this.removeReplacement(replacement.id);
                    clearTimeout(this.timeout);
                }, 10000);
            } catch (e) {
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    title: this.$t('errors.generationError')
                });
                this.error = e;
                console.log(e);
            }
        },

        async replaceAll() {
            this.loading = true;
            this.error = null;

            try {
                const { status, msg: detail } = await replaceAllParts(this.bookId, {
                    ...this.replaceParams
                });

                if (!status) {
                    this.error = [{ detail }];
                    return;
                }

                this.timeout = setTimeout(() => {
                    this.loading = false;
                    this.stage = 'success';
                    clearTimeout(this.timeout);
                }, this.replacements.length * 5000);
            } catch (e) {
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    title: this.$t('errors.generationError')
                });
                this.error = e;
                this.loading = false;
                console.log(e);
            }
        },

        prevMatch() {
            let { length } = this.replacements;
            let prev = --this.position;

            if (prev < 0) prev = --length;
            this.position = prev;
        },

        nextMatch() {
            let { length } = this.replacements;
            let next = ++this.position;

            if (next >= length) next = 0;
            this.position = next;
        },

        viewFullList() {
            this.viewAll = true;
            this.diffMode = true;
        },

        onMessage({ code, params = {} }) {
            if (code !== 200) {
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    title: this.$t('errors.generationError')
                });

                return;
            }

            if (params.action === 'text_replace' && params.book_id === this.bookId) {
                clearTimeout(this.timeout);

                const { extra_data, completed, is_completed, total } = params;
                this.total = total;
                this.completed = completed;

                if (extra_data && extra_data.bp_ids) {
                    extra_data.bp_ids.forEach(id => this.removeReplacement(id));
                }

                if (is_completed) {
                    if (this.replacements.length > 1 && total === 1 && total === completed) {
                        this.loading = false;
                        return;
                    }

                    if (total === completed) {
                        this.loading = false;
                        this.stage = 'success';
                    }
                }
            }
        },

        removeReplacement(id) {
            const index = this.replacements.findIndex(rep => rep.id === id);

            if (index >= 0) {
                this.replacements.splice(index, 1);
            }
        },

        ok() {
            this.$emit('update');
            this.opened = false;
        },

        onReplaceParamsWordChange() {
            this.fetchReplacements();
        }
    }
};
</script>

<style lang="scss" src="./MassReplacement.scss"></style>
